import React, { useState, useEffect } from "react";
import { StyleSheet, View, ScrollView, Image, Platform, TouchableOpacity } from "react-native";
import * as ImagePicker from "expo-image-picker";
import { useFirebase } from "@components/firebase/useFirebase";
import { createMedia } from "@/services/firebase/media-functions";
import { extractMediaMetadata } from "@/services/media-metadata";
import { useAuth } from "@components/auth/useAuth";
import { ThemedText } from "@/components/ThemedText";
import { ThemedView } from "@/components/ThemedView";
import ThemedScreen from "@/components/ThemedScreen";
import { CustomAlert } from "@/components/ThemedAlert";
import { ThemedProgressBar } from "@/components/ThemedProgressBar";
import { ThemedButton } from "@/components/ThemedButton";
import { readFileAsBase64 } from "@/utils/file-reader";
import { useRouter } from "expo-router";

interface IUploadedFile {
  downloadUrl: string;
  contentType: string;
  fileName: string;
}

interface IUploadItem {
  name: string;
  contentType: string;
  size?: number;
  uri?: string;
  base64?: string | null;
  file?: File;
}

/**
 * Renders a cross-platform preview for an uploaded media item.
 *
 * @param {object} props - Component properties.
 * @param {string} props.url - Media download URL.
 * @param {string} props.contentType - MIME type of the media.
 * @returns {React.JSX.Element} The rendered preview element.
 */
function LocalMediaPreview({ url, contentType }: { url: string; contentType: string }) {
  if (contentType.startsWith("audio/")) {
    if (Platform.OS === "web") {
      return (
        <View style={styles.audioContainer}>
          <ThemedText style={styles.playerLabel}>🎵 Audio Playback</ThemedText>
          {React.createElement("audio", {
            src: url,
            controls: true,
            style: { width: "100%", outline: "none" },
          })}
        </View>
      );
    }
    return (
      <View style={styles.audioContainer}>
        <ThemedText style={styles.playerLabel}>🎵 Audio Uploaded</ThemedText>
        <ThemedText style={styles.mediaUrlText} numberOfLines={1}>
          {url}
        </ThemedText>
      </View>
    );
  }
  if (contentType.startsWith("video/")) {
    if (Platform.OS === "web") {
      return (
        <View style={styles.videoContainer}>
          {React.createElement("video", {
            src: url,
            controls: true,
            style: { width: "100%", height: 120, objectFit: "contain", borderRadius: 8 },
          })}
        </View>
      );
    }
    return (
      <View style={styles.videoContainer}>
        <ThemedText style={[styles.playerLabel, { color: "#fff", padding: 8 }]}>🎬 Video Uploaded</ThemedText>
        <ThemedText style={[styles.mediaUrlText, { color: "#ccc", padding: 8 }]} numberOfLines={1}>
          {url}
        </ThemedText>
      </View>
    );
  }
  return <Image source={{ uri: url }} style={styles.thumbnail} />;
}

/**
 * Test Media Storage Screen component.
 * Allows testing media uploads to Firebase Storage across Web, Android, and iOS.
 *
 * @returns {React.JSX.Element} Test media storage screen.
 */
export default function TestMediaScreen() {
  const router = useRouter();
  const services = useFirebase();
  const { user } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [uploadedFiles, setUploadedFiles] = useState<IUploadedFile[]>([]);

  useEffect(() => {
    if (Platform.OS !== "web" || typeof window === "undefined") {
      return;
    }

    const handleDragOver = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
    };

    const handleDrop = async (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();

      const files = e.dataTransfer?.files;
      if (files && files.length > 0) {
        for (const file of Array.from(files)) {
          await handleUploadItem({
            name: file.name,
            contentType: file.type,
            size: file.size,
            file,
          });
        }
      }
    };

    window.addEventListener("dragover", handleDragOver);
    window.addEventListener("drop", handleDrop);

    return () => {
      window.removeEventListener("dragover", handleDragOver);
      window.removeEventListener("drop", handleDrop);
    };
  }, [services, user]);

  /**
   * Uploads an item (Web File or Native Asset) to Firebase Storage and registers it in Firestore.
   *
   * @param {IUploadItem} item - Media item descriptors.
   */
  const handleUploadItem = async (item: IUploadItem) => {
    if (!services || !user) {
      CustomAlert.alert("Error", "Services or User not initialized");
      return;
    }

    const isValid =
      item.contentType.startsWith("image/") ||
      item.contentType.startsWith("audio/") ||
      item.contentType.startsWith("video/");
    if (!isValid) {
      CustomAlert.alert("Invalid File", "Please select an image, audio, or video file.");
      return;
    }

    setUploading(true);
    setProgress(0);

    try {
      let base64 = item.base64;
      if (!base64) {
        if (item.uri) {
          base64 = await readFileAsBase64(item.uri);
        } else if (item.file) {
          base64 = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(item.file!);
            reader.onload = () => {
              const result = reader.result as string;
              resolve(result.split(",")[1]);
            };
            reader.onerror = (error) => reject(error);
          });
        }
      }

      if (!base64) {
        throw new Error("Could not extract base64 data from selected media.");
      }

      setProgress(40);

      const metadataTarget = item.file || { name: item.name, fileName: item.name, uri: item.uri };
      const [metadataResult] = await Promise.allSettled([
        extractMediaMetadata(metadataTarget, item.contentType),
      ]);
      const extractedMetadata = metadataResult.status === "fulfilled" ? metadataResult.value : undefined;
      setProgress(60);

      const res = await createMedia(services, {
        base64,
        title: item.name,
        name: item.name,
        contentType: item.contentType,
        metadata: extractedMetadata,
        skipAutoMemory: true,
      });

      if (res.status && res.downloadUrl) {
        setUploadedFiles((prev) => [
          { downloadUrl: res.downloadUrl!, contentType: item.contentType, fileName: item.name },
          ...prev,
        ]);
        setProgress(100);
        CustomAlert.alert("Success", "Media uploaded via server successfully!");
      } else {
        if (res.isDuplicate || res.error?.includes("already been uploaded")) {
          const mediaId = res.existingMediaId;
          const mediaUrl = res.existingMediaUrl || (mediaId ? `/my-media/${mediaId}` : "");

          console.warn("[Media Upload] Duplicate file detected: This has already been uploaded.", {
            fileName: item.name,
            error: res.error,
            existingMediaId: mediaId,
            existingMediaUrl: mediaUrl,
          });

          const buttons = [
            ...(mediaId
              ? [
                  {
                    text: "View Media",
                    onPress: () => {
                      router.push(`/my-media/${mediaId}`);
                    },
                  },
                ]
              : []),
            { text: "Dismiss", style: "cancel" as const },
          ];

          CustomAlert.alert(
            "Upload Warning",
            mediaUrl
              ? `This has already been uploaded.\n\nMedia URL: ${mediaUrl}`
              : "This has already been uploaded.",
            buttons,
            mediaUrl && mediaId
              ? {
                  link: {
                    label: `Open ${mediaUrl}`,
                    url: mediaUrl,
                    onPress: () => {
                      router.push(`/my-media/${mediaId}`);
                    },
                  },
                }
              : undefined
          );
        } else {
          CustomAlert.alert("Upload Failed", res.error || "Unknown error");
        }
      }
    } catch (error: any) {
      console.error("Upload error:", error);
      CustomAlert.alert("Upload Failed", error.message || "Unknown error occurred");
    } finally {
      setUploading(false);
    }
  };

  /**
   * Opens the media library picker for images and videos.
   */
  const handlePickMedia = async () => {
    try {
      const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
      if (status !== ImagePicker.PermissionStatus.GRANTED && (status as any) !== "granted") {
        CustomAlert.alert("Permission Required", "Please grant photo and video library access to select media.");
        return;
      }

      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ["images", "videos"],
        allowsMultipleSelection: true,
        quality: 1.0,
      });

      if (result.canceled || !result.assets || result.assets.length === 0) {
        return;
      }

      for (const asset of result.assets) {
        const isVideo = asset.type === "video" || (asset.mimeType && asset.mimeType.startsWith("video/"));
        const defaultMime = isVideo ? "video/mp4" : "image/jpeg";
        const mime = asset.mimeType || defaultMime;
        const ext = isVideo ? "mp4" : "jpg";
        const fileName = asset.fileName || `media_${Date.now()}.${ext}`;

        await handleUploadItem({
          name: fileName,
          contentType: mime,
          size: asset.fileSize,
          uri: asset.uri,
          base64: asset.base64,
        });
      }
    } catch (err: any) {
      console.error("[TestMediaScreen] Media picker error:", err);
      CustomAlert.alert("Picker Error", err.message || "Failed to select media files.");
    }
  };

  /**
   * Opens the camera to capture a photo or video.
   */
  const handleTakePhoto = async () => {
    try {
      const { status } = await ImagePicker.requestCameraPermissionsAsync();
      if (status !== ImagePicker.PermissionStatus.GRANTED && (status as any) !== "granted") {
        CustomAlert.alert("Permission Required", "Please grant camera access to take a photo or video.");
        return;
      }

      const result = await ImagePicker.launchCameraAsync({
        mediaTypes: ["images", "videos"],
        quality: 1.0,
      });

      if (result.canceled || !result.assets || result.assets.length === 0) {
        return;
      }

      for (const asset of result.assets) {
        const isVideo = asset.type === "video" || (asset.mimeType && asset.mimeType.startsWith("video/"));
        const defaultMime = isVideo ? "video/mp4" : "image/jpeg";
        const mime = asset.mimeType || defaultMime;
        const ext = isVideo ? "mp4" : "jpg";
        const fileName = asset.fileName || `capture_${Date.now()}.${ext}`;

        await handleUploadItem({
          name: fileName,
          contentType: mime,
          size: asset.fileSize,
          uri: asset.uri,
          base64: asset.base64,
        });
      }
    } catch (err: any) {
      console.error("[TestMediaScreen] Camera capture error:", err);
      CustomAlert.alert("Camera Error", err.message || "Failed to capture media.");
    }
  };

  return (
    <ThemedScreen
      title="Test Media Storage"
      leftIcon={{
        name: "arrow-back",
        onPress: () => router.navigate("/admin/test" as any),
      }}
    >
      <ScrollView style={styles.container}>
        <ThemedView style={styles.card}>
          <ThemedText type="subtitle">Upload Test</ThemedText>
          <ThemedText style={styles.infoText}>
            {Platform.OS === "web"
              ? "Drop an image, audio, or video file anywhere on this screen, or select a file to upload to Firebase Storage."
              : "Select or capture an image or video to test uploading to Firebase Storage."}
          </ThemedText>

          <TouchableOpacity
            activeOpacity={0.7}
            onPress={handlePickMedia}
            disabled={uploading}
            style={[styles.dropZone, uploading && styles.dropZoneActive]}
          >
            {uploading ? (
              <View style={styles.progressContainer}>
                <View style={{ width: 150 }}>
                  <ThemedProgressBar loading={uploading} />
                </View>
                <ThemedText style={styles.progressText}>{Math.round(progress)}%</ThemedText>
              </View>
            ) : (
              <View style={{ alignItems: "center", gap: 6 }}>
                <ThemedText style={styles.dropZoneText}>
                  {Platform.OS === "web" ? "📁 Drop Media Here or Click to Select" : "📁 Tap Here to Select Media"}
                </ThemedText>
                <ThemedText style={styles.dropZoneSubtext}>Supports photos, videos, and audio</ThemedText>
              </View>
            )}
          </TouchableOpacity>

          <View style={styles.buttonRow}>
            <ThemedButton
              title="Choose from Library"
              onPress={handlePickMedia}
              disabled={uploading}
              style={styles.actionBtn}
            />
            <ThemedButton
              title="Take Photo / Video"
              variant="secondary"
              onPress={handleTakePhoto}
              disabled={uploading}
              style={styles.actionBtn}
            />
          </View>
        </ThemedView>

        {uploadedFiles.length > 0 && (
          <ThemedView style={[styles.card, { marginTop: 20 }]}>
            <ThemedText type="subtitle">Recently Uploaded</ThemedText>
            <View style={styles.gallery}>
              {uploadedFiles.map((file, index) => (
                <View key={index} style={styles.galleryItem}>
                  <ThemedText style={styles.fileNameText} numberOfLines={1}>
                    {file.fileName}
                  </ThemedText>
                  <LocalMediaPreview url={file.downloadUrl} contentType={file.contentType} />
                </View>
              ))}
            </View>
          </ThemedView>
        )}
      </ScrollView>
    </ThemedScreen>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 16,
  },
  card: {
    padding: 20,
    borderRadius: 16,
    backgroundColor: "rgba(150, 150, 150, 0.05)",
    borderWidth: 1,
    borderColor: "rgba(150, 150, 150, 0.1)",
  },
  infoText: {
    fontSize: 14,
    opacity: 0.7,
    marginVertical: 12,
  },
  dropZone: {
    height: 150,
    borderWidth: 2,
    borderRadius: 12,
    borderStyle: "dashed",
    borderColor: "#4A90E2",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "rgba(74, 144, 226, 0.05)",
    marginTop: 10,
  },
  dropZoneActive: {
    backgroundColor: "rgba(74, 144, 226, 0.1)",
    borderStyle: "solid",
  },
  dropZoneText: {
    fontSize: 16,
    color: "#4A90E2",
    fontWeight: "600",
  },
  dropZoneSubtext: {
    fontSize: 12,
    opacity: 0.6,
  },
  buttonRow: {
    flexDirection: "row",
    gap: 10,
    marginTop: 14,
  },
  actionBtn: {
    flex: 1,
  },
  progressContainer: {
    alignItems: "center",
  },
  progressText: {
    marginTop: 8,
    fontWeight: "bold",
    color: "#4A90E2",
  },
  gallery: {
    flexDirection: "column",
    gap: 16,
    marginTop: 16,
  },
  galleryItem: {
    backgroundColor: "rgba(0,0,0,0.02)",
    padding: 12,
    borderRadius: 10,
    gap: 8,
  },
  fileNameText: {
    fontSize: 12,
    fontWeight: "bold",
    opacity: 0.6,
  },
  thumbnail: {
    width: 100,
    height: 100,
    borderRadius: 8,
    backgroundColor: "#eee",
  },
  audioContainer: {
    width: "100%",
    padding: 8,
    backgroundColor: "rgba(74, 144, 226, 0.05)",
    borderRadius: 8,
    gap: 6,
  },
  playerLabel: {
    fontSize: 11,
    opacity: 0.5,
  },
  mediaUrlText: {
    fontSize: 11,
    opacity: 0.6,
  },
  videoContainer: {
    width: "100%",
    backgroundColor: "#000",
    borderRadius: 8,
    overflow: "hidden",
  },
});

